import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Award, RotateCcw, Sparkles, PlayCircle, Share2 } from "lucide-react";
import MissionCard from "@/components/MissionCard";
import CEOCityMap from "@/components/CEOCityMap";
import LemonBotJr from "@/components/LemonBotJr";
import StickerBook from "@/components/StickerBook";
import {
  loadJrState,
  resetJrState,
  saveJrState,
  JR_BIRDS,
  JR_PROBLEM_OPTIONS,
  SAMPLE_COMPLETED_STATE,
} from "@/utils/jrDemoState";
import { computeMissionStars, computeBadges } from "@/utils/jrScoring";

const MISSIONS = [
  { n: 1, t: "Welcome to CEO City", d: "Discover what a Junior CEO does and meet your first big idea.", route: "/elementary/demo/mission/1" },
  { n: 2, t: "Meet the Biz Birds", d: "Find the bird that matches how you like to lead and help.", route: "/elementary/demo/mission/2" },
  { n: 3, t: "Find a Problem", d: "Pick a problem you can help solve at school, home, or in your community.", route: "/elementary/demo/mission/3" },
  { n: 4, t: "Create Your Big Idea", d: "Turn your problem into a product and name the customer it helps.", route: "/elementary/demo/mission/4" },
  { n: 5, t: "Brand Your Business", d: "Pick a name, color, and symbol for your logo.", route: "/elementary/demo/mission/5" },
  { n: 6, t: "Money Mission", d: "Drag coins to match the price — count like a real CEO!", route: "/elementary/demo/mission/6" },
  { n: 7, t: "Tell the World", d: "Design a sign with a slogan and bold colors.", route: "/elementary/demo/mission/7" },
  { n: 8, t: "Pitch Day", d: "Write your 30-second Junior CEO pitch.", route: "/elementary/demo/mission/8" },
  { n: 9, t: "Junior CEO Certificate", d: "Type your name & see your animated Junior CEO Certificate!", route: "/elementary/demo/mission/9" },
];

export default function ElementaryDashboard() {
  const navigate = useNavigate();
  const [state, setState] = useState(loadJrState());

  // Refresh state on focus (e.g., after returning from a mission page).
  useEffect(() => {
    const onFocus = () => setState(loadJrState());
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onFocus);
    };
  }, []);

  const completed = new Set(state.completedMissions || []);
  const statusFor = (m) => {
    if (completed.has(m.n)) return "completed";
    if (m.preview) return "preview";
    return "unlocked";
  };

  const showPlanPreview = completed.has(3);
  const allDone = completed.size === MISSIONS.length;

  const handleReset = () => {
    if (window.confirm("Reset your demo progress? This will clear your answers.")) {
      resetJrState();
      setState(loadJrState());
    }
  };

  const handleFullDemo = () => {
    saveJrState(SAMPLE_COMPLETED_STATE);
    setState(loadJrState());
    // Scroll to the plan preview after the state flip.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="elementary-jr-plan"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const birdLabel =
    state.bizBird && JR_BIRDS[state.bizBird]
      ? `${JR_BIRDS[state.bizBird].emoji} ${JR_BIRDS[state.bizBird].name}`
      : "Not chosen yet";

  const ideaLabel = (() => {
    // Prefer an explicit businessIdea (populated by "Try Full Demo Flow" or
    // future Mission 4+). Fall back to the Mission-3 problem-choice label
    // so the original 3-mission flow still shows something meaningful.
    if (state.businessIdea) return state.businessIdea;
    if (!state.problemChoice) return "In progress";
    if (state.problemChoice === "custom") return state.customProblem || "Your own idea";
    const opt = JR_PROBLEM_OPTIONS.find((o) => o.key === state.problemChoice);
    return opt ? opt.label : "In progress";
  })();

  // "My Problem" always reflects the problem statement — never the business idea.
  const problemLabel = (() => {
    if (state.problem) return state.problem;
    if (!state.problemChoice) return "—";
    if (state.problemChoice === "custom") return state.customProblem || "—";
    const opt = JR_PROBLEM_OPTIONS.find((o) => o.key === state.problemChoice);
    return opt ? opt.label : "—";
  })();

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 via-amber-50 to-white">
      {/* Top bar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/lemonade-leaders-jr")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
            data-testid="elementary-demo-back"
          >
            <ArrowLeft className="w-4 h-4" />
            About Lemonade Leaders Jr.
          </button>
          <div className="text-xs sm:text-sm text-gray-500 italic">Demo Mode · No login</div>
          <button
            onClick={handleReset}
            className="text-gray-500 hover:text-gray-800 text-sm flex items-center gap-1"
            data-testid="elementary-demo-reset"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </nav>

      {/* Header */}
      <header className="pt-28 pb-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 rounded-3xl p-8 sm:p-10 shadow-xl text-white">
            <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              <Sparkles className="w-4 h-4" />
              Junior CEO Adventure
            </div>
            <h1 className="text-3xl sm:text-5xl font-bold mb-3">Welcome to CEO City</h1>
            <p className="text-yellow-50 text-lg sm:text-xl">
              Complete your missions and become a Junior CEO.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-sm">
              <span className="bg-white/20 px-3 py-1 rounded-full font-semibold">
                {completed.size} / {MISSIONS.length} missions complete
              </span>
              {state.bizBird && (
                <span className="bg-white/20 px-3 py-1 rounded-full font-semibold">
                  Biz Bird: {birdLabel}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mission grid */}
      <main className="px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          {/* LemonBot Jr. — friendly static helper */}
          <div className="mb-6">
            <LemonBotJr
              greeting="Hi, I'm LemonBot Jr. — your Junior CEO guide! Pick a mission card below and I'll help you the whole way."
              prompts={[
                "Start with Mission 1: pick a problem you care about.",
                "Then pick your Biz Bird in Mission 2.",
                "Finish Mission 3 to see your first Junior CEO Plan appear below!",
              ]}
            />
          </div>

          {/* Demo control strip — lets reviewers jump to a finished state */}
          <div className="mb-8 flex flex-wrap items-center justify-between gap-3 bg-white rounded-2xl ring-1 ring-yellow-200 px-5 py-3 shadow-sm">
            <p className="text-sm text-gray-600">
              <strong className="text-gray-900">Reviewing for a school or funder?</strong>{" "}
              Preview the full finished journey in one click — nothing is saved beyond your browser.
            </p>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={handleFullDemo}
                className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-full font-semibold text-sm transition-colors"
                data-testid="elementary-demo-full"
              >
                <PlayCircle className="w-4 h-4" />
                Try Full Demo Flow
              </button>
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-full font-semibold text-sm ring-1 ring-gray-200"
                data-testid="elementary-demo-reset-inline"
              >
                <RotateCcw className="w-4 h-4" />
                Reset Demo
              </button>
            </div>
          </div>

          <div className="flex items-baseline justify-between mb-4">
            <span className="text-sm text-gray-500 ml-auto hidden sm:inline">
              {completed.size} of {MISSIONS.length} done
            </span>
          </div>
          <CEOCityMap
            missions={MISSIONS}
            statusFor={statusFor}
            stars={(n) => computeMissionStars(state, n)}
            onSelect={(m) => m.route && navigate(m.route)}
            testId="elementary-ceo-city-map"
          />

          {/* Mini Business Plan preview — appears after Mission 3 */}
          {showPlanPreview && (
            <section className="mt-12">
              <div className="bg-white rounded-3xl p-7 sm:p-9 ring-2 ring-yellow-300 shadow-lg" data-testid="elementary-jr-plan">
                <div className="flex items-center gap-3 mb-1">
                  <Award className="w-7 h-7 text-yellow-500" />
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    My Junior CEO Plan
                  </h2>
                  <span
                    className={`ml-auto text-xs font-bold px-3 py-1 rounded-full ${
                      allDone
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {allDone ? "Complete Preview" : "Status: In Progress"}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-6 italic">
                  Student name: <span className="font-semibold text-gray-700 not-italic">Junior CEO</span>
                </p>

                {/* Your Progress — kid-friendly + educator-friendly */}
                <div className="mb-6" data-testid="jr-plan-progress">
                  <div className="flex items-baseline justify-between mb-2">
                    <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide">
                      Your Progress
                    </p>
                    <p
                      className="text-sm font-semibold text-gray-700"
                      data-testid="jr-plan-progress-label"
                    >
                      {completed.size} of {MISSIONS.length} missions complete{" "}
                      <span className="text-gray-400 font-normal">
                        ({Math.round((completed.size / MISSIONS.length) * 100)}%)
                      </span>
                    </p>
                  </div>
                  <div
                    className="h-3 w-full rounded-full bg-yellow-50 ring-1 ring-yellow-100 overflow-hidden"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={MISSIONS.length}
                    aria-valuenow={completed.size}
                    aria-label={`Your progress: ${completed.size} of ${MISSIONS.length} missions complete`}
                  >
                    <div
                      className={`h-full rounded-full transition-all duration-500 ease-out ${
                        allDone
                          ? "bg-gradient-to-r from-green-400 to-emerald-500"
                          : "bg-gradient-to-r from-yellow-400 to-orange-500"
                      }`}
                      style={{
                        width: `${(completed.size / MISSIONS.length) * 100}%`,
                      }}
                      data-testid="jr-plan-progress-bar"
                    />
                  </div>
                </div>

                <dl className="grid sm:grid-cols-3 gap-5">
                  <div className="bg-yellow-50 rounded-2xl p-5">
                    <dt className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-2">
                      My Problem
                    </dt>
                    <dd className="text-gray-900 font-medium leading-relaxed">
                      {problemLabel}
                    </dd>
                  </div>
                  <div className="bg-amber-50 rounded-2xl p-5">
                    <dt className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
                      My Biz Bird
                    </dt>
                    <dd className="text-gray-900 font-medium flex items-center gap-3 flex-wrap">
                      {state.bizBird && JR_BIRDS[state.bizBird] ? (
                        <>
                          <img
                            src={JR_BIRDS[state.bizBird].image}
                            alt=""
                            aria-hidden="true"
                            className="w-12 h-12 rounded-full object-contain bg-white ring-1 ring-amber-200"
                            loading="lazy"
                            decoding="async"
                          />
                          <span>{JR_BIRDS[state.bizBird].name}</span>
                        </>
                      ) : (
                        <span>Not chosen yet</span>
                      )}
                    </dd>
                  </div>
                  <div className="bg-orange-50 rounded-2xl p-5">
                    <dt className="text-xs font-bold text-orange-700 uppercase tracking-wide mb-2">
                      My Business Idea
                    </dt>
                    <dd className="text-gray-900 font-medium">{ideaLabel}</dd>
                  </div>
                  {state.customer && (
                    <div className="bg-green-50 rounded-2xl p-5">
                      <dt className="text-xs font-bold text-green-700 uppercase tracking-wide mb-2">
                        My Customer
                      </dt>
                      <dd className="text-gray-900 font-medium">{state.customer}</dd>
                    </div>
                  )}
                  {state.helpKind && (
                    <div className="bg-rose-50 rounded-2xl p-5">
                      <dt className="text-xs font-bold text-rose-700 uppercase tracking-wide mb-2">
                        How I Help
                      </dt>
                      <dd className="text-gray-900 font-medium">{state.helpKind}</dd>
                    </div>
                  )}
                  {state.pitch && (
                    <div className="bg-yellow-50/70 rounded-2xl p-5 sm:col-span-2">
                      <dt className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-2">
                        Pitch Preview
                      </dt>
                      <dd className="text-gray-900 italic leading-relaxed">
                        "{state.pitch}"
                      </dd>
                    </div>
                  )}
                  {state.brandName && (
                    <div className="bg-pink-50 rounded-2xl p-5">
                      <dt className="text-xs font-bold text-pink-700 uppercase tracking-wide mb-2">
                        My Brand
                      </dt>
                      <dd className="text-gray-900 font-medium">{state.brandName}</dd>
                    </div>
                  )}
                  {state.signSlogan && (
                    <div className="bg-violet-50 rounded-2xl p-5">
                      <dt className="text-xs font-bold text-violet-700 uppercase tracking-wide mb-2">
                        My Slogan
                      </dt>
                      <dd className="text-gray-900 font-medium">"{state.signSlogan}"</dd>
                    </div>
                  )}
                  {state.coinGameBestRounds > 0 && (
                    <div className="bg-emerald-50 rounded-2xl p-5">
                      <dt className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-2">
                        Money Mission
                      </dt>
                      <dd className="text-gray-900 font-medium">
                        {state.coinGameBestRounds}/3 rounds solved
                        {state.coinGameUsedHelper ? " (used helper)" : " — no helper!"}
                      </dd>
                    </div>
                  )}
                </dl>

                {/* Plan CTAs */}
                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    onClick={() => {
                      const birdParam = state.bizBird || "eagle";
                      const businessParam = encodeURIComponent(
                        ideaLabel && ideaLabel !== "In progress" ? ideaLabel : "Pencil Power Station"
                      );
                      navigate(
                        `/elementary/demo/certificate-preview?name=Junior%20CEO&business=${businessParam}&bird=${birdParam}`
                      );
                    }}
                    className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
                    data-testid="jr-plan-view-cert"
                  >
                    <Award className="w-4 h-4" />
                    View Certificate Preview
                  </button>
                  <button
                    onClick={() => navigate("/share/elementary-preview")}
                    className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-800 px-5 py-2.5 rounded-full font-semibold text-sm ring-1 ring-gray-200 transition-colors"
                    data-testid="jr-plan-share-sample"
                  >
                    <Share2 className="w-4 h-4" />
                    Share Sample Plan
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Sticker Book — earned badges */}
          <StickerBook unlocked={computeBadges(state)} />

          {/* Junior CEO Certificate — clickable preview */}
          <section className="mt-10">
            <button
              onClick={() => navigate("/elementary/demo/certificate-preview")}
              className={`w-full rounded-3xl p-7 ring-2 transition-all text-left ${
                allDone
                  ? "bg-gradient-to-br from-yellow-100 to-orange-100 ring-yellow-400 hover:shadow-xl"
                  : "bg-slate-50 ring-slate-200 hover:ring-yellow-300 hover:shadow-md"
              } flex flex-col sm:flex-row items-center gap-5`}
              data-testid="elementary-cert-card"
            >
              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 ${
                  allDone ? "bg-yellow-500 text-white" : "bg-yellow-100 text-yellow-600"
                }`}
              >
                <Award className="w-8 h-8" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  Junior CEO Certificate
                </h3>
                <p className="text-gray-600 text-sm">
                  {allDone
                    ? "You've finished every mission — your certificate is ready!"
                    : "Keep going! Complete your missions to unlock your Junior CEO Certificate."}
                </p>
              </div>
              <span
                className={`text-xs font-bold px-3 py-1.5 rounded-full ${
                  allDone ? "bg-yellow-500 text-white" : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {allDone ? "Ready" : "Preview"}
              </span>
            </button>
          </section>
        </div>
      </main>
    </div>
  );
}
